import {
  DestinatioType,
  IExperienceTemplateResource,
  InternalStorageInstructionType,
  ISubTaskQuestionTemplate,
  QuestionType,
  StorageContext,
  SubTaskDataDestinationSupportingActor,
  SubTaskQuestionTemplateQuestionSupportingActor,
  SupportingActorType,
  TextQuestionValidationType
} from '@dadohr/shared/domainTypes/experience';
import {
  isScaleQuestion,
  LINEAR_SCALE_MAX_RANGE,
  LINEAR_SCALE_MIN_VALUES,
  RATING_MAX_RANGE,
  SCALE_ENDPOINT_LABEL_MAX_LENGTH
} from '@dadohr/shared/domainTypes/questionScale';
import {
  ScheduleChannel,
  ScheduleChannelType
} from '@dadohr/shared/domainTypes/schedules';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography
} from '@material-ui/core';
import arrayMove from 'array-move';
import { AlertCircle, GripVertical, HelpCircle, X } from 'lucide-react';
import React, { SetStateAction, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import shortid from 'shortid';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import { useId } from '../../../../../../../../hooks/useId';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import { getSelectableSupportingActors } from '../../../../../../../../services/experiences/actorUsage';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_QUESTION
} from '../../../validation';
import { StorageDestinations } from './dataDestinations';

const OptionBuilderList = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const Option = styled('div')((p) => ({
  boxSizing: 'border-box',
  marginBottom: p.theme.spacing(),
  background: p.theme.palette.background.paper,
  width: '100%',
  display: 'grid',
  alignItems: 'center',
  gridGap: p.theme.spacing(),
  gridTemplateColumns: '1fr min-content min-content'
}));

const WarningBox = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(),
  padding: p.theme.spacing(2),
  backgroundColor: '#FEFAEC',
  borderRadius: 4,
  marginTop: p.theme.spacing(2)
}));

export const OptionBuilder = <T extends any>({
  value,
  onChange,
  textToNewOption,
  optionToKey,
  optionToLabel,
  onLabelChange,
  label,
  error,
  helperText,
  onBlur
}: {
  value: T[];
  onChange: (nextValue: T[]) => void;
  textToNewOption: (str: string) => T;
  optionToKey: (o: T, i: number) => string;
  optionToLabel: (o: T, i: number) => string;
  onLabelChange?: (o: T, nextLabel: string, i: number) => void;
  label: React.ReactNode;
  error?: boolean;
  helperText?: string;
  onBlur?: TextFieldProps['onBlur'];
}) => {
  const id = useId();
  const [input, setInput] = useState('');
  const theme = useTheme();
  return (
    <>
      <OptionBuilderList>
        <DragDropContext
          onDragEnd={(r) => {
            if (
              !r.destination ||
              r.destination.droppableId !== id ||
              r.source.droppableId !== id
            ) {
              return;
            }

            const nextItems = arrayMove(
              value,
              r.source.index,
              r.destination.index
            );
            onChange(nextItems);
          }}
        >
          <Droppable droppableId={id}>
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {value.map((item, index) => {
                  const key = optionToKey(item, index);
                  const label = optionToLabel(item, index);
                  return (
                    <Draggable key={key} draggableId={key} index={index}>
                      {(provided, snapshot) => (
                        <Option
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}
                        >
                          <TextField
                            variant="outlined"
                            value={label}
                            InputProps={{
                              readOnly: !onLabelChange
                            }}
                            onChange={(ev) => {
                              onLabelChange?.(item, ev.target.value, index);
                            }}
                            onBlur={onLabelChange ? onBlur : undefined}
                          />
                          <GripVertical
                            width={20}
                            height={20}
                            color={theme.custom.colors.gray.w500}
                          />
                          <IconButton
                            onClick={() =>
                              onChange(value.filter((x) => x !== item))
                            }
                          >
                            <X size={20} />
                          </IconButton>
                        </Option>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <TextField
          label={label}
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText}
          value={input}
          onChange={(ev) => setInput(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              onChange([...value, textToNewOption(input)]);
              setInput('');
            }
          }}
          onBlur={onBlur}
        />
      </OptionBuilderList>
    </>
  );
};

export const BlockSettingsSubTaskQuestion = ({
  value,
  onChange,
  validation,
  setValidation,
  channels,
  resources,
  onChangeResources,
  expTpl,
  customFields,
  goToSubPage,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskQuestionTemplate;
  onChange: (nextValue: ISubTaskQuestionTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  channels: ScheduleChannel[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const validateQuestion = useFieldValidator(
    setValidation,
    VALIDATORS_QUESTION,
    'question'
  );
  const invalidQuestionReason = validation?.fields.question || null;

  const validateAnswerOptions = useFieldValidator(
    setValidation,
    VALIDATORS_QUESTION,
    'answerOptions'
  );
  const invalidAnswerOptions = validation?.fields.answerOptions || null;

  const validateStorages = useFieldValidator(
    setValidation,
    VALIDATORS_QUESTION,
    'storage'
  );

  const validatePlaceholder = useFieldValidator(
    setValidation,
    VALIDATORS_QUESTION,
    'placeholder'
  );
  const invalidPlaceholder = validation?.fields.placeholder || null;

  const validateScale = useFieldValidator(
    setValidation,
    VALIDATORS_QUESTION,
    'scale'
  );
  const invalidScale = validation?.fields.scale || null;

  const updateOptions = (nextOptions: { id: string; label: string }[]) => {
    const prevOptions =
      value.data.question.type === 'select' ? value.data.question.options : []; // always true

    const nextValue = {
      ...value,
      data: {
        ...value.data,
        question: {
          ...value.data.question,
          options: nextOptions
        }
      }
    };

    (invalidAnswerOptions ||
      // be sure to always check removals as they might not trigger the blur event properly
      nextOptions.length < prevOptions.length) &&
      validateAnswerOptions(nextValue, expTpl);
    return onChange(nextValue);
  };

  const actorOptions = getSelectableSupportingActors(
    expTpl.supportingActors
  ).filter((a) => a.type !== SupportingActorType.CHANNEL); // used to be filtered down to actor.type === 'assignedBySubtask' - we now open this up

  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextFieldWithReplacementTokens
            variant="outlined"
            label={t('subTask.question.dialog.label.label')}
            value={value.data.question.label}
            placeholder={t('Enter a question...')}
            error={!!invalidQuestionReason}
            helperText={invalidQuestionReason}
            onBlur={() => validateQuestion(value, expTpl)}
            onChange={(nextV) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  question: {
                    ...value.data.question,
                    label: nextV
                  }
                }
              };
              invalidQuestionReason && validateQuestion(nextValue, expTpl);
              return onChange(nextValue);
            }}
            fullWidth
            tokens={tokens}
          />
        </FormRow>
        {value.data.question.type === 'text' && (
          <>
            <FormRow>
              <TextField
                variant="outlined"
                label={t('subTask.question.dialog.placeholder.label')}
                value={value.data.question.placeholder}
                placeholder={t('Enter a placeholder text...')}
                error={!!invalidPlaceholder}
                helperText={invalidPlaceholder}
                onBlur={() => validatePlaceholder(value, expTpl)}
                onChange={(ev) => {
                  const nextValue = {
                    ...value,
                    data: {
                      ...value.data,
                      question: {
                        ...value.data.question,
                        placeholder: ev.target.value
                      }
                    }
                  };
                  invalidPlaceholder && validatePlaceholder(nextValue, expTpl);
                  return onChange(nextValue);
                }}
                fullWidth
              />
            </FormRow>
            <FormRow>
              <div>
                <Typography variant="h5" style={{ marginTop: 16, marginBottom: 16 }}>
                  {t('Answer validation')}
                </Typography>
                <RadioGroup
                  value={
                    value.data.question.validationType ||
                    TextQuestionValidationType.NONE
                  }
                  onChange={(event) => {
                    const nextValue = {
                      ...value,
                      data: {
                        ...value.data,
                        question: {
                          ...value.data.question,
                          validationType: event.target
                            .value as TextQuestionValidationType
                        }
                      }
                    };
                    onChange(nextValue);
                  }}
                >
                  <FormControlLabel
                    value={TextQuestionValidationType.NONE}
                    control={<Radio />}
                    label={t('None - any answer permitted')}
                    style={{ marginBottom: 8 }}
                  />
                  <FormControlLabel
                    value={TextQuestionValidationType.DATE}
                    control={<Radio />}
                    label={t('Date')}
                    style={{ marginBottom: 8 }}
                  />
                  <FormControlLabel
                    value={TextQuestionValidationType.EMAIL}
                    control={<Radio />}
                    label={t('Email address')}
                    style={{ marginBottom: 8 }}
                  />
                </RadioGroup>
                {value.data.question.validationType === TextQuestionValidationType.EMAIL && (() => {
                  const hasSlack = channels.some(c => c.type === ScheduleChannelType.SLACK);
                  const hasTeams = channels.some(c => c.type === ScheduleChannelType.MS_TEAMS);

                  if (!hasSlack && !hasTeams) {
                    return null;
                  }

                  let warningMessage = '';
                  if (hasSlack && hasTeams) {
                    warningMessage = t('Email address validation is not available in Slack and Teams notifications. If assignees complete this task in Slack or Teams, Dado will not be able to ensure they provide an email address.');
                  } else if (hasSlack) {
                    warningMessage = t('Email address validation is not available in Slack notifications. If assignees complete this task in Slack, Dado will not be able to ensure they provide an email address.');
                  } else {
                    warningMessage = t('Email address validation is not available in Teams notifications. If assignees complete this task in Teams, Dado will not be able to ensure they provide an email address.');
                  }

                  return (
                    <WarningBox>
                      <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                      <Typography variant="body2">
                        {warningMessage}
                      </Typography>
                    </WarningBox>
                  );
                })()}
              </div>
            </FormRow>
            {(value.data.question.validationType === TextQuestionValidationType.NONE ||
              !value.data.question.validationType) && (
              <FormRow>
                <div>
                  <FormControlLabel
                    label={
                      <FlexContainer alignItems="center">
                        {t('Multi-line answer')}
                      </FlexContainer>
                    }
                    control={
                      <Switch
                        checked={value.data.question.rows === 4}
                        onChange={(ev) => {
                          const nextValue = {
                            ...value,
                            data: {
                              ...value.data,
                              question: {
                                ...value.data.question,
                                rows: ev.target.checked ? 4 : 1
                              }
                            }
                          };
                          onChange(nextValue);
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    label={
                      <FlexContainer alignItems="center">
                        {t('Hide answer after submitting')}
                        <Tooltip title="After the participant submits an answer, it will be hidden to ensure privacy">
                          <HelpCircle
                            size={16}
                            color={theme.custom.colors.gray.w500}
                          />
                        </Tooltip>
                      </FlexContainer>
                    }
                    control={
                      <Switch
                        checked={value.data.question.sensitive}
                        onChange={(ev) => {
                          const nextValue = {
                            ...value,
                            data: {
                              ...value.data,
                              question: {
                                ...value.data.question,
                                sensitive: ev.target.checked
                              }
                            }
                          };
                          onChange(nextValue);
                        }}
                      />
                    }
                  />
                </div>
              </FormRow>
            )}
          </>
        )}
        {value.data.question.type === QuestionType.SUPPORTING_ACTOR && (
          <>
            <FormRow>
              <Select
                variant="outlined"
                fullWidth
                value={value.data.question.supportingActorToAssign}
                disabled={!actorOptions.length}
                onChange={(event) => {
                  const nextV = event.target.value as string;

                  const question: SubTaskQuestionTemplateQuestionSupportingActor = {
                    ...value.data.question,
                    type: QuestionType.SUPPORTING_ACTOR,
                    supportingActorToAssign: nextV
                  };
                  const newStorages = [...value.data.storages];
                  const supportingActorDataDestination = newStorages.find(
                    (s) =>
                      s.type === InternalStorageInstructionType.SUPPORTING_ACTOR
                  ) as SubTaskDataDestinationSupportingActor;
                  if (!supportingActorDataDestination) {
                    const newDataDestination: SubTaskDataDestinationSupportingActor = {
                      type: InternalStorageInstructionType.SUPPORTING_ACTOR,
                      destinationType: DestinatioType.SUBTASK_ANSWER,
                      supportingActorToAssign: nextV
                    };
                    newStorages.push(newDataDestination);
                  } else {
                    supportingActorDataDestination.supportingActorToAssign = nextV;
                  }

                  const nextValue: ISubTaskQuestionTemplate = {
                    ...value,
                    data: {
                      ...value.data,
                      question,
                      storages: newStorages
                    }
                  };
                  invalidQuestionReason && validateQuestion(nextValue, expTpl);
                  validateStorages(nextValue, expTpl);
                  return onChange(nextValue);
                }}
              >
                {actorOptions.map((assignedActor) => (
                  <MenuItem value={assignedActor.key}>
                    {assignedActor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormRow>
            {!actorOptions.length && (
              <InfoBox color="yellow">
                {t(
                  'To use this subtask, first add a supporting actor to this experience'
                )}
              </InfoBox>
            )}
          </>
        )}
        {value.data.question.type === 'address' && (
          <>
            <FormRow>
              <FormControlLabel
                label={
                  <FlexContainer alignItems="center">
                    {t(`Don't ask for name`)}
                  </FlexContainer>
                }
                control={
                  <Switch
                    checked={value.data.question.excludedFields?.includes(
                      'name'
                    )}
                    onChange={(ev) => {
                      if (value.data.question.type !== 'address') {
                        // can never happen
                        return;
                      }
                      const exclusionSet = new Set(
                        value.data.question.excludedFields || []
                      );
                      if (ev.target.checked) {
                        exclusionSet.add('name');
                      } else {
                        exclusionSet.delete('name');
                      }

                      const nextValue = {
                        ...value,
                        data: {
                          ...value.data,
                          question: {
                            ...value.data.question,
                            excludedFields: [...exclusionSet]
                          }
                        }
                      };
                      onChange(nextValue);
                    }}
                  />
                }
              />
            </FormRow>
            <FormRow>
              <FormControlLabel
                label={
                  <FlexContainer alignItems="center">
                    {t('Hide address after submitting')}
                    <Tooltip title="After the participant submits an address, it will be hidden to ensure privacy">
                      <HelpCircle
                        size={16}
                        color={theme.custom.colors.gray.w500}
                      />
                    </Tooltip>
                  </FlexContainer>
                }
                control={
                  <Switch
                    checked={value.data.question.sensitive}
                    onChange={(ev) => {
                      const nextValue = {
                        ...value,
                        data: {
                          ...value.data,
                          question: {
                            ...value.data.question,
                            sensitive: ev.target.checked
                          }
                        }
                      };
                      onChange(nextValue);
                    }}
                  />
                }
              />
            </FormRow>
          </>
        )}
        {(value.data.question.type === 'select' ||
          value.data.question.type === 'multiSelect') && (
          <>
            <OptionBuilder
              label={t('Add a new option')}
              error={!!invalidAnswerOptions}
              helperText={
                invalidAnswerOptions ||
                t('Type an option and press Enter to confirm')
              }
              value={value.data.question.options}
              onBlur={() => {
                return validateAnswerOptions(value, expTpl);
              }}
              onChange={updateOptions}
              onLabelChange={(o, nextLabel, i) => {
                if (
                  value.data.question.type !== 'select' &&
                  value.data.question.type !== 'multiSelect'
                ) {
                  // can never happen

                  return;
                }
                const nextOptions = [...value.data.question.options];
                const nextOption = { ...o, label: nextLabel };
                nextOptions[i] = nextOption;
                updateOptions(nextOptions);
              }}
              textToNewOption={(label) => ({ id: shortid(), label })}
              optionToKey={(o) => o.id}
              optionToLabel={(o) => o.label}
            />
            <FormControlLabel
              label={t('Allow multiple answers')}
              control={
                <Switch
                  checked={value.data.question.type === 'multiSelect'}
                  onChange={() => {
                    onChange({
                      ...value,
                      data: {
                        ...value.data,
                        question: {
                          ...value.data.question,
                          type:
                            value.data.question.type === 'select'
                              ? 'multiSelect'
                              : 'select'
                        } as any
                      }
                    });
                  }}
                  color="primary"
                />
              }
            />
          </>
        )}
        {value.data.question.type === QuestionType.LINEAR_SCALE && (
          <>
            <FormRow>
              <FlexContainer spacing={2} alignItems="center">
                <Typography variant="body2">{t('From')}</Typography>
                <Select
                  variant="outlined"
                  value={value.data.question.min}
                  onChange={(ev) => {
                    if (value.data.question.type !== QuestionType.LINEAR_SCALE) {
                      return;
                    }
                    const nextValue: ISubTaskQuestionTemplate = {
                      ...value,
                      data: {
                        ...value.data,
                        question: {
                          ...value.data.question,
                          min: Number(ev.target.value) as 0 | 1
                        }
                      }
                    };
                    validateScale(nextValue, expTpl);
                    onChange(nextValue);
                  }}
                >
                  {LINEAR_SCALE_MIN_VALUES.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2">{t('to')}</Typography>
                <Select
                  variant="outlined"
                  value={value.data.question.max}
                  onChange={(ev) => {
                    if (value.data.question.type !== QuestionType.LINEAR_SCALE) {
                      return;
                    }
                    const nextValue: ISubTaskQuestionTemplate = {
                      ...value,
                      data: {
                        ...value.data,
                        question: {
                          ...value.data.question,
                          max: Number(ev.target.value)
                        }
                      }
                    };
                    validateScale(nextValue, expTpl);
                    onChange(nextValue);
                  }}
                >
                  {Array.from(
                    {
                      length:
                        LINEAR_SCALE_MAX_RANGE.max - LINEAR_SCALE_MAX_RANGE.min + 1
                    },
                    (_, i) => LINEAR_SCALE_MAX_RANGE.min + i
                  ).map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FlexContainer>
            </FormRow>
            <FormRow>
              <TextField
                variant="outlined"
                fullWidth
                label={t('Label for {{ value }} (optional)', {
                  value: value.data.question.min
                })}
                value={value.data.question.minLabel || ''}
                error={!!invalidScale}
                inputProps={{ maxLength: SCALE_ENDPOINT_LABEL_MAX_LENGTH + 1 }}
                onBlur={() => validateScale(value, expTpl)}
                onChange={(ev) => {
                  if (value.data.question.type !== QuestionType.LINEAR_SCALE) {
                    return;
                  }
                  const nextValue: ISubTaskQuestionTemplate = {
                    ...value,
                    data: {
                      ...value.data,
                      question: {
                        ...value.data.question,
                        minLabel: ev.target.value
                      }
                    }
                  };
                  invalidScale && validateScale(nextValue, expTpl);
                  onChange(nextValue);
                }}
              />
            </FormRow>
            <FormRow>
              <TextField
                variant="outlined"
                fullWidth
                label={t('Label for {{ value }} (optional)', {
                  value: value.data.question.max
                })}
                value={value.data.question.maxLabel || ''}
                error={!!invalidScale}
                helperText={invalidScale}
                inputProps={{ maxLength: SCALE_ENDPOINT_LABEL_MAX_LENGTH + 1 }}
                onBlur={() => validateScale(value, expTpl)}
                onChange={(ev) => {
                  if (value.data.question.type !== QuestionType.LINEAR_SCALE) {
                    return;
                  }
                  const nextValue: ISubTaskQuestionTemplate = {
                    ...value,
                    data: {
                      ...value.data,
                      question: {
                        ...value.data.question,
                        maxLabel: ev.target.value
                      }
                    }
                  };
                  invalidScale && validateScale(nextValue, expTpl);
                  onChange(nextValue);
                }}
              />
            </FormRow>
          </>
        )}
        {value.data.question.type === QuestionType.RATING && (
          <FormRow>
            <FlexContainer spacing={2} alignItems="center">
              <Typography variant="body2">{t('Number of stars')}</Typography>
              <Select
                variant="outlined"
                value={value.data.question.max}
                onChange={(ev) => {
                  if (value.data.question.type !== QuestionType.RATING) {
                    return;
                  }
                  const nextValue: ISubTaskQuestionTemplate = {
                    ...value,
                    data: {
                      ...value.data,
                      question: {
                        ...value.data.question,
                        max: Number(ev.target.value)
                      }
                    }
                  };
                  validateScale(nextValue, expTpl);
                  onChange(nextValue);
                }}
              >
                {Array.from(
                  { length: RATING_MAX_RANGE.max - RATING_MAX_RANGE.min + 1 },
                  (_, i) => RATING_MAX_RANGE.min + i
                ).map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FlexContainer>
          </FormRow>
        )}
      </FormRowGroup>
      {showOnCompleteHookSettings && (
        <FormRowGroup>
          <Typography variant={'h5'}>{t('Data Destinations')}</Typography>
          <StorageDestinations
            isCompletionHookDataDestination={false}
            tokens={tokens}
            value={value.data.storages}
            disallowUserProfile={
              value.data.question.type === QuestionType.SUPPORTING_ACTOR
            }
            onChange={(nextStorages) => {
              const nV: ISubTaskQuestionTemplate = {
                ...value,
                data: {
                  ...value.data,
                  storages: nextStorages
                }
              };
              validateStorages(nV, expTpl);
              onChange(nV);
            }}
            resources={resources}
            onChangeResources={onChangeResources}
            expTpl={expTpl}
            customFields={customFields}
            goToSubPage={goToSubPage}
            validation={validation}
            storageContext={StorageContext.SUBTASK}
          />
        </FormRowGroup>
      )}
    </>
  );
};

export const BlockSettingsSubTaskQuestionReadOnly = ({
  value,
  resources,
  expTpl,
  customFields,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskQuestionTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      {value.data.question.label && (
        <FormRow>
          <DataFieldWithLabel label={t('subTask.question.dialog.label.label')}>
            {value.data.question.label}
          </DataFieldWithLabel>
        </FormRow>
      )}
      {value.data.question.type === 'select' && (
        <FormRow>
          <DataFieldWithLabel label={t('Answer options')}>
            {value.data.question.options.map((o) => o.label).join(' • ')}
          </DataFieldWithLabel>
        </FormRow>
      )}
      {isScaleQuestion(value.data.question) && (
        <FormRow>
          <DataFieldWithLabel label={t('Scale')}>
            {value.data.question.type === QuestionType.LINEAR_SCALE
              ? [
                  `${value.data.question.min} – ${value.data.question.max}`,
                  value.data.question.minLabel || value.data.question.maxLabel
                    ? `(${value.data.question.minLabel || '…'} → ${
                        value.data.question.maxLabel || '…'
                      })`
                    : ''
                ]
                  .filter(Boolean)
                  .join(' ')
              : t('{{ count }} stars', { count: value.data.question.max })}
          </DataFieldWithLabel>
        </FormRow>
      )}

      <br />

      <Typography variant={'h6'}>{t('Data Destinations')}</Typography>
      {showOnCompleteHookSettings && (
        <StorageDestinations
          tokens={tokens}
          isCompletionHookDataDestination={false}
          disallowUserProfile={
            value.data.question.type === QuestionType.SUPPORTING_ACTOR
          }
          value={value.data.storages}
          resources={resources}
          expTpl={expTpl}
          customFields={customFields}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};
